<template>
  <div
    :class="[
      'entry',
      { 'entry--main': isMainEntry },
      { 'entry--disabled': disabled },
    ]"
  >
    <div class="entry__header">
      <div class="entry__header-title">
        {{ title }}
        <span v-if="showSuggestionText" class="entry__header-title--suggested">
          ({{ $t('ai.SUGGESTED') }})
        </span>
      </div>
    </div>
    <transition name="fade" mode="out-in">
      <BaseLoading v-if="isLoading" :delay="0" />
      <p
        v-else
        :key="contentText"
        v-sanitize-html="contentText"
        :class="['entry__value', { 'entry__value--empty': !content }]"
      />
    </transition>

    <div v-if="showActions" class="entry__action-buttons">
      <BaseButton v-if="!isMainEntry" outline size="small" @click="cancel">
        {{ $t('buttons.CLOSE') }}
      </BaseButton>

      <BaseButton v-if="canRetry" size="small" outline @click="retry">
        {{ $t('buttons.RETRY') }}
      </BaseButton>

      <BaseButton
        theme="purple"
        :disabled="disabled"
        size="small"
        @click="useSuggestion"
      >
        {{ buttonText }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import BaseButton from '../base/BaseButton.vue'
import BaseLoading from '../base/BaseLoading.vue'

export default {
  components: {
    BaseButton,
    BaseLoading,
  },

  props: {
    title: {
      type: String,
      required: false,
      default: undefined,
    },

    content: {
      type: String,
      required: false,
      default: undefined,
    },

    hasAiText: {
      type: Boolean,
      required: false,
      default: false,
    },

    canRetry: {
      type: Boolean,
      required: false,
      default: false,
    },

    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },

    isMainEntry: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['onUseSuggestion', 'onRetry', 'onCancel'],
  computed: {
    contentText() {
      return this.content ? this.content : this.$t('ai.INPUT_EMPTY')
    },

    showSuggestionText() {
      return this.isMainEntry && this.hasAiText
    },

    showActions() {
      return this.hasAiText && !this.isLoading
    },

    buttonText() {
      return this.isMainEntry
        ? this.$t('ai.USE_SUGGESTION')
        : this.$t('buttons.APPROVE')
    },
  },

  methods: {
    useSuggestion() {
      this.$emit('onUseSuggestion')
    },

    retry() {
      this.$emit('onRetry')
    },

    cancel() {
      this.$emit('onCancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.entry {
  display: flex;
  flex-direction: column;
  gap: $spacing-5xs;
  background: $color-paper;
  border: 1px solid $color-border;
  padding: $spacing-2xs;
  border-radius: $spacing-3xs;
  border-top-left-radius: 0;

  &--main {
    border-radius: $spacing-3xs;
  }

  &--disabled {
    background: $color-pebble;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__header-title {
    color: $color-text;
    font-size: 14px;
    font-weight: 400;
    &--suggested {
      color: $color-purple-60;
    }
  }

  &__value {
    &--empty {
      color: $color-text-light;
      font-style: italic;
    }
  }

  &__action-buttons {
    @include form-action-buttons-layout;
    gap: $spacing-4xs;
    margin-top: $spacing-3xs;
  }
}
</style>
